import { sha256 } from 'js-sha256';
import { DEFAULT_CONFIG } from './types.js';
/**
 * Generate 3-5 pseudorandom points on the grid from hash
 */
function generateOriginPoints(hash, size) {
    const origins = [];
    // Use first 20 characters of hash for origin generation
    const originHash = hash.substring(0, 20);
    // Determine number of origins (3-5)
    const numOrigins = 3 + (parseInt(originHash.substring(0, 2), 16) % 3);
    // Generate each origin point
    for (let i = 0; i < numOrigins; i++) {
        const segmentStart = 2 + i * 4;
        const segment = originHash.substring(segmentStart, segmentStart + 4);
        if (segment.length < 4)
            break;
        // Generate coordinates (avoid edges for better blob formation)
        const margin = Math.floor(size * 0.1); // 10% margin from edges
        const effectiveSize = size - 2 * margin;
        const x = margin + Math.floor((parseInt(segment.substring(0, 2), 16) / 255) * effectiveSize);
        const y = margin + Math.floor((parseInt(segment.substring(2, 4), 16) / 255) * effectiveSize);
        // Select color from palette
        const colorIndex = parseInt(segment, 16) % DEFAULT_CONFIG.palette.length;
        const color = DEFAULT_CONFIG.palette[colorIndex];
        // Generate intensity (0.5 to 1.0)
        const intensity = 0.5 + (parseInt(segment.substring(1, 3), 16) % 128) / 255;
        origins.push({
            point: { x, y },
            color,
            intensity
        });
    }
    return origins;
}
/**
 * Calculate distance between two points
 */
function calculateDistance(p1, p2) {
    const dx = p1.x - p2.x;
    const dy = p1.y - p2.y;
    return Math.sqrt(dx * dx + dy * dy);
}
/**
 * Cellular automata system with smooth color blending
 */
function applyCellularAutomataGradient(grid, origins, size, sharpness = 1.0) {
    // For each pixel, calculate influence from all origins and blend colors
    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            const pixelPoint = { x, y };
            let totalInfluence = 0;
            const blendedColor = [0, 0, 0];
            // Calculate influence from each origin
            const influences = [];
            origins.forEach((origin) => {
                const distance = calculateDistance(pixelPoint, origin.point);
                // Use inverse distance weighting with falloff
                // Closer points have exponentially higher influence
                const maxDistance = Math.sqrt(size * size + size * size);
                const normalizedDistance = distance / maxDistance;
                // Exponential falloff for very distinct origin points
                // Higher exponent = sharper boundaries, lower = more blending
                // Apply sharpness multiplier to the base falloff exponent
                const falloffExponent = 8.0 * sharpness;
                const influence = Math.pow(1 - Math.min(normalizedDistance, 0.99), falloffExponent) * origin.intensity;
                influences.push({
                    origin,
                    influence,
                    distance
                });
                totalInfluence += influence;
            });
            // Blend colors based on weighted influences
            if (totalInfluence > 0) {
                influences.forEach(({ origin, influence }) => {
                    const weight = influence / totalInfluence;
                    blendedColor[0] += origin.color[0] * weight;
                    blendedColor[1] += origin.color[1] * weight;
                    blendedColor[2] += origin.color[2] * weight;
                });
                // Apply overall brightness based on total influence
                // Areas with high total influence (near origins) are brighter
                // Areas with low total influence (far from all origins) are dimmer
                const maxPossibleInfluence = Math.max(...origins.map((o) => o.intensity));
                let brightnessMultiplier = Math.min(1, totalInfluence / maxPossibleInfluence);
                // Apply additional sharpening to make origins very distinct
                // Lower exponent = more dramatic contrast, adjusted by sharpness
                const brightnessExponent = 0.4 / Math.max(0.1, sharpness);
                brightnessMultiplier = Math.pow(brightnessMultiplier, brightnessExponent);
                // Allow much darker areas to increase contrast
                const finalBrightness = Math.max(0.05, brightnessMultiplier);
                grid[y][x] = [
                    Math.round(Math.min(255, blendedColor[0] * finalBrightness)),
                    Math.round(Math.min(255, blendedColor[1] * finalBrightness)),
                    Math.round(Math.min(255, blendedColor[2] * finalBrightness))
                ];
            }
            else {
                // Fallback for areas with no influence
                grid[y][x] = [0, 0, 0];
            }
        }
    }
}
/**
 * Generate a hash-based avatar as an RGB grid
 * @param input - Input data to generate avatar from
 * @param config - Configuration options for avatar generation
 * @returns 2D array of RGB values representing the avatar
 */
export function generateHashatar(input, config = {}) {
    // Merge with default configuration
    const finalConfig = {
        ...DEFAULT_CONFIG,
        ...config,
    };
    const { size } = finalConfig;
    // make sha256 hash of input
    const hash = sha256(input);
    // Initialize empty grid (black background)
    const grid = Array(size)
        .fill(null)
        .map(() => Array(size)
        .fill(null)
        .map(() => [0, 0, 0]));
    // Generate 3-5 origin points from hash
    const origins = generateOriginPoints(hash, size);
    // Apply cellular automata gradient effects
    applyCellularAutomataGradient(grid, origins, size, finalConfig.sharpness);
    return grid;
}
/**
 * Convert RGB grid to a flat array format
 * @param grid - The RGB grid to flatten
 * @returns Flat array in format [r1, g1, b1, r2, g2, b2, ...]
 */
export function flattenRGBGrid(grid) {
    const flat = [];
    for (const row of grid) {
        for (const pixel of row) {
            flat.push(...pixel);
        }
    }
    return flat;
}
/**
 * Get dimensions of an RGB grid
 * @param grid - The RGB grid to measure
 * @returns Object with width and height properties
 */
export function getGridDimensions(grid) {
    const height = grid.length;
    const width = height > 0 ? grid[0].length : 0;
    return { width, height };
}
/**
 * Extract origin points used for generating a hashatar
 * @param input - Input data to generate avatar from
 * @param size - Size of the avatar grid (defaults to DEFAULT_CONFIG.size)
 * @returns Array of origin points with their coordinates
 */
export function extractOriginPoints(input, size = DEFAULT_CONFIG.size) {
    const hash = sha256(input);
    const origins = generateOriginPoints(hash, size);
    return origins.map(origin => origin.point);
}
/**
 * Extract component colors used for generating a hashatar
 * @param input - Input data to generate avatar from
 * @param size - Size of the avatar grid (defaults to DEFAULT_CONFIG.size)
 * @returns Array of unique colors used in the hashatar
 */
export function extractComponentColors(input, size = DEFAULT_CONFIG.size) {
    const hash = sha256(input);
    const origins = generateOriginPoints(hash, size);
    return origins.map(origin => origin.color);
}
//# sourceMappingURL=generator.js.map